import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { Button, Container, Dropdown, Card, Badge, Spinner, Alert } from 'react-bootstrap';

import { useNavigate, useLocation } from 'react-router-dom';
import Config from '../../config.json'

import SiteStyles from '../Site.module.css'
import TextStyles from '../TextStyles.module.css'

const ViewComments = (props) => {
    const navigate = useNavigate();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    const fetchComments = async (page = 1) => {
        try {
            setLoading(true);
            const response = await axios.get(`${Config.API_BASE_URL}/comment/admin/latest`, {
                params: { page, limit: 100 },
            });

            setComments(response.data.comments);
            setPagination(response.data.pagination);
            setCurrentPage(page);
            setError(null);
        } catch (err) {
            setError('Failed to fetch comments: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComments(1);
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchComments(newPage);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getScoreColor = (upvotes, downvotes) => {
        const score = upvotes - downvotes;
        if (score > 0) return 'success';
        if (score < 0) return 'danger';
        return 'secondary';
    };

    if (loading && comments.length === 0) {
        return (
            <Container>
                <div className="text-center mt-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p>Loading comments...</p>
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <div>
                <br />
                <button
                    onClick={() => { navigate('/course') }}>← Back</button>
                <br />
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h1 className={TextStyles.Title} style={{ marginBottom: 8 }}>
                        Latest Comments {pagination.totalComments && `(${pagination.totalComments} total)`}
                    </h1>
                    {loading && <Spinner animation="border" size="sm" />}
                </div>

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <div style={{ width: '100%', borderRadius: 5, backgroundColor: 'rgb(245,245,255)', padding: 16 }}>
                    {comments.length === 0 ? (
                        <p className="text-center text-muted">No comments found.</p>
                    ) : (
                        <>
                            {comments.map((comment) => (
                                <Card key={comment._id} className="mb-2" style={{ border: '1px solid #dee2e6' }}>
                                    <Card.Body className="py-2">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div style={{ flex: 1 }}>
                                                <div className="d-flex align-items-center mb-1">
                                                    <strong style={{ fontSize: '0.9em', color: '#495057' }}>
                                                        {comment.displayName} ({comment.userName})
                                                    </strong>
                                                    {comment.isReply && (
                                                        <Badge bg="info" className="ms-2" style={{ fontSize: '0.7em' }}>
                                                            Reply
                                                        </Badge>
                                                    )}
                                                    <span className="text-muted ms-2" style={{ fontSize: '0.8em' }}>
                                                        {formatDate(comment.createdAt)}
                                                    </span>
                                                </div>

                                                <p className="mb-1" style={{ fontSize: '0.9em', lineHeight: '1.3' }}>
                                                    {comment.body}
                                                </p>

                                                <div className="d-flex align-items-center" style={{ fontSize: '0.8em' }}>
                                                    <span className="text-muted me-3">
                                                        Thread: <code>{comment.threadId || 'Unknown'}</code>
                                                    </span>
                                                    <span className="text-muted me-3">
                                                        User ID: <code>{comment.userId?.slice(-8) || 'N/A'}</code>
                                                    </span>
                                                    <span className="text-muted me-3">
                                                        Email: <code>{comment.userEmail}</code>
                                                    </span>
                                                    <Badge
                                                        bg={getScoreColor(comment.upvotes, comment.downvotes)}
                                                        style={{ fontSize: '0.7em' }}
                                                    >
                                                        ↑{comment.upvotes} ↓{comment.downvotes}
                                                        (Score: {comment.upvotes - comment.downvotes})
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="d-flex justify-content-center align-items-center mt-3">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        disabled={!pagination.hasPrevPage || loading}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        className="me-2"
                                    >
                                        Previous
                                    </Button>

                                    <span className="mx-3" style={{ fontSize: '0.9em' }}>
                                        Page {pagination.currentPage} of {pagination.totalPages}
                                    </span>

                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        disabled={!pagination.hasNextPage || loading}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        className="ms-2"
                                    >
                                        Next
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Container>
    );
};

const mapStateToProps = state => {
    return {
        token: state.auth?.token // Adjust based on your Redux state structure
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(ViewComments);